// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ProjectLineItem } from '@/project/api/types';
import { ONE_DAY_MS } from '@/utilities/time-constants';
import { DateTime } from 'luxon';

interface DemandStep {
  /** Instant the step transitions. The value applies at and after this time. */
  timeMs: number;
  /** Running dated demand just after `timeMs`, excluding the baseline. */
  datedQuantity: number;
}

export interface DemandScheduleRow {
  sku: string;
  itemName: string;
  /** Constant demand from dateless line items; always active. */
  baseline: number;
  /** Maximum total demand (baseline + dated peak) across the timeline. */
  peak: number;
  /** Instant the peak is reached, or null when the SKU has only baseline demand. */
  peakTimeMs: number | null;
  /** Step transitions for the dated-demand curve. Empty when baseline-only. */
  steps: DemandStep[];
  /** True when any dated line item contributes to this SKU. */
  hasDatedDemand: boolean;
}

interface DemandSchedule {
  rows: DemandScheduleRow[];
  /** Earliest dated start across every SKU, or null when no SKUs have dated demand. */
  rangeStartMs: number | null;
  /** Latest dated end across every SKU, or null when no SKUs have dated demand. */
  rangeEndMs: number | null;
}

interface DatedDemand {
  startMs: number;
  endMs: number;
  quantity: number;
}

interface DemandGroup {
  itemName: string;
  baseline: number;
  dated: DatedDemand[];
}

interface SweepEvent {
  timeMs: number;
  priority: number;
  delta: number;
}

// Interval conventions
// --------------------
// Storage:    [start_on, end_on]         — closed on both ends. end_on is the
//                                          last active day, inclusive.
// Sweep:      [startMs,  endMs + 1 day)  — half-open; left-inclusive,
//                                          right-exclusive. End event lands at
//                                          endMs + 1 day so the interval still
//                                          covers endOn itself. Two touching
//                                          phases (B.start = A.end + 1 day)
//                                          share the same boundary day; with
//                                          END firing before START at that
//                                          boundary the transition collapses
//                                          into a single step — no zero-width
//                                          dip between back-to-back phases.
// Rendering: visx step-after — a data point (t, v) makes v take effect AT t
//            and hold until the next point. This matches the sweep's
//            left-inclusive / right-exclusive semantics exactly: the new
//            value is inclusive at the boundary, the previous value is
//            exclusive past it.
const END_PRIORITY = 0;
const START_PRIORITY = 1;

/**
 * Builds the per-SKU demand time series for a project. Dated line items
 * contribute to a sweep-line step curve; dateless line items contribute to a
 * constant baseline. The shape of the curve and the peak both match the
 * semantics used by `aggregateFulfillmentBySku` so numbers agree across tabs.
 *
 * The schedule's x-range starts from the project start (if set) and extends
 * to the project end (if set), widened by any dated line items that fall
 * outside that window so every datum is visible.
 */
export function buildDemandSchedule(
  lineItems: ProjectLineItem[],
  projectStartMs: number | null = null,
  projectEndMs: number | null = null
): DemandSchedule {
  const groups = new Map<string, DemandGroup>();
  let rangeStartMs: number | null = projectStartMs;
  let rangeEndMs: number | null = projectEndMs;

  for (const lineItem of lineItems) {
    // Service (non-physical) line items don't flow through FOs and have no
    // representation on the Fulfillment by SKU tab. Skipping them here keeps
    // Demand Schedule's Peak in sync with Fulfillment by SKU's Project
    // requested — the backend SQL applies the same i.is_physical = TRUE
    // filter on both sides.
    if (!lineItem.item.isPhysical) continue;
    const { sku } = lineItem.item;
    const { quantity } = lineItem;
    if (quantity === null || quantity <= 0) continue;

    const group = getOrCreate(groups, sku, () => ({
      itemName: lineItem.item.name,
      baseline: 0,
      dated: [],
    }));

    const startMs = dateToMs(lineItem.startOn);
    const endMs = dateToMs(lineItem.endOn);

    // Treat a line item as dated only when both endpoints are present. A
    // half-dated line item is ambiguous enough that "always active" is the
    // conservative choice — matches aggregateFulfillmentBySku.
    if (startMs !== null && endMs !== null) {
      group.dated.push({ startMs, endMs, quantity });
      if (rangeStartMs === null || startMs < rangeStartMs) {
        rangeStartMs = startMs;
      }
      if (rangeEndMs === null || endMs > rangeEndMs) {
        rangeEndMs = endMs;
      }
    } else {
      group.baseline += quantity;
    }
  }

  const rows: DemandScheduleRow[] = [];
  for (const [sku, group] of groups) {
    const { steps, peakDatedQuantity, peakTimeMs } = buildSteps(group.dated);
    rows.push({
      sku,
      itemName: group.itemName,
      baseline: group.baseline,
      peak: group.baseline + peakDatedQuantity,
      peakTimeMs,
      steps,
      hasDatedDemand: group.dated.length > 0,
    });
  }
  rows.sort((a, b) => a.sku.localeCompare(b.sku));

  return { rows, rangeStartMs, rangeEndMs };
}

function buildSteps(dated: DatedDemand[]): {
  steps: DemandStep[];
  peakDatedQuantity: number;
  peakTimeMs: number | null;
} {
  if (dated.length === 0) {
    return { steps: [], peakDatedQuantity: 0, peakTimeMs: null };
  }

  const events: SweepEvent[] = [];
  for (const { startMs, endMs, quantity } of dated) {
    events.push({ timeMs: startMs, priority: START_PRIORITY, delta: quantity });
    // Half-open [start, end + 1 day): phase is active through endMs, drops the
    // day after. Touching phases share this boundary day, and the end-first
    // priority keeps the transition clean instead of dipping to zero.
    events.push({
      timeMs: endMs + ONE_DAY_MS,
      priority: END_PRIORITY,
      delta: -quantity,
    });
  }
  events.sort((a, b) =>
    a.timeMs === b.timeMs ? a.priority - b.priority : a.timeMs - b.timeMs
  );

  let running = 0;
  let peakDatedQuantity = 0;
  let peakTimeMs: number | null = null;
  const steps: DemandStep[] = [];

  let index = 0;
  while (index < events.length) {
    const { timeMs } = events[index];
    // Apply every event at this timeMs before recording a step so same-instant
    // starts and ends collapse into a single transition.
    while (index < events.length && events[index].timeMs === timeMs) {
      running += events[index].delta;
      index += 1;
    }
    steps.push({ timeMs, datedQuantity: running });
    if (running > peakDatedQuantity) {
      peakDatedQuantity = running;
      peakTimeMs = timeMs;
    }
  }

  return { steps, peakDatedQuantity, peakTimeMs };
}

function dateToMs(value: DateTime | null): number | null {
  if (value === null) return null;
  const dateKey = value.toISODate();
  const ms = Date.parse(dateKey);
  return Number.isNaN(ms) ? null : ms;
}

function getOrCreate<K, V>(map: Map<K, V>, key: K, factory: () => V): V {
  const existing = map.get(key);
  if (existing !== undefined) return existing;
  const created = factory();
  map.set(key, created);
  return created;
}
